import { useEffect, useRef } from 'react';
import { Interface } from 'ethers';
import { v4 as uuidv4 } from 'uuid';
import { contractAddress } from '../../config/contract';
import { useEthToUsd } from '../../hooks/useEthToUsd';
import WertWidget from '@wert-io/widget-initializer';

interface CardPaymentProps {
  amount: string;
  userAddress?: string;
  referralAddress: string;
  stake: boolean;
  onError: (error: string) => void;
  onSuccess: (hash: string) => void;
  onProcessing: (processing: boolean) => void;
}

export function CardPayment({
  amount,
  userAddress,
  referralAddress,
  stake,
  onError,
  onProcessing,
  onSuccess,
}: CardPaymentProps) {
  const ethToUsd = useEthToUsd();
  const wertContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    return () => {
      if (wertContainerRef.current) {
        wertContainerRef.current.remove();
      }
    };
  }, []);

  const handlePayment = async () => {
    if (!userAddress) {
      onError('Please connect your wallet');
      return;
    }

    // Check if the address is a valid EVM address
    if (!userAddress.startsWith('0x')) {
      onError('Card payments are only supported for Ethereum and BSC networks');
      return;
    }

    // Check if referrer address is valid for EVM (Card payments use EVM chain)
    if (referralAddress && !referralAddress.startsWith('0x')) {
      // Just log the error, don't show to user
      console.error('Invalid referrer address for Card payments');
      return;
    }

    const inputAmount = parseFloat(amount);
    if (!amount || isNaN(inputAmount) || inputAmount <= 0) {
      onError('Please enter a valid amount');
      return;
    }

    if (!ethToUsd) {
      onError('Unable to fetch ETH price');
      return;
    }

    try {
      onProcessing(true);

      const requiredEth = Number((inputAmount / ethToUsd).toFixed(8));

      const iface = new Interface([
        'function buyWithETHWert(address _user, bool stake, address referral) payable',
      ]);

      const sc_input_data = iface.encodeFunctionData('buyWithETHWert', [
        userAddress,
        stake,
        referralAddress,
      ]);

      const unsignedData = {
        address: userAddress,
        commodity: 'ETH',
        network: 'ethereum',
        commodity_amount: requiredEth,
        sc_address: contractAddress.eth,
        sc_input_data,
      };

      const response = await fetch('https://quantumgenesis.io/api/sign', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(unsignedData),
      });

      if (!response.ok) throw new Error('Failed to sign smart contract data');

      const signedData = await response.json();

      // Create overlay container
      let container = document.getElementById('wert-widget-overlay') as HTMLDivElement;
      if (!container) {
        container = document.createElement('div');
        container.id = 'wert-widget-overlay';

        Object.assign(container.style, {
          position: 'fixed',
          top: '0',
          left: '0',
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 11, 31, 0.8)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: '999999',
          animation: 'fadeIn 0.3s ease-out',
        });

        // Create the inner widget container
        const widgetContainer = document.createElement('div');
        widgetContainer.id = 'wert-widget';

        const isMobile = window.matchMedia('(max-width: 768px)').matches;

        Object.assign(widgetContainer.style, {
          width: '480px',
          maxWidth: '100%',
          height: '680px',
          maxHeight: isMobile ? '90%' : '80%',
          backgroundColor: '#000B1F',
          borderRadius: isMobile ? '16px 16px 0 0' : '12px',
          border: '1px solid rgba(0, 246, 255, 0.2)',
          boxShadow: '0 8px 32px rgba(0, 246, 255, 0.15)',
          overflow: 'hidden',
          position: 'relative',
          animation: isMobile ? 'slideUp 0.3s ease-out' : 'fadeIn 0.3s ease-out',
        });

        // Add animation styles
        const style = document.createElement('style');
        style.textContent = `
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes slideUp {
            from { transform: translateY(100%); }
            to { transform: translateY(0); }
          }
        `;
        document.head.appendChild(style);

        // Close overlay on click outside the widget
        container.onclick = (e) => {
          if (e.target === container) {
            container?.remove();
            style.remove();
          }
        };

        container.appendChild(widgetContainer);
        document.body.appendChild(container);
        wertContainerRef.current = container;
      }

      const widgetConfig = {
        ...signedData,
        partner_id: '01JKGVY3S49AD9JJ18H37BPYFE',
        container_id: 'wert-widget',
        click_id: uuidv4(),
        origin: 'https://widget.wert.io',
        theme: {
          mode: 'dark',
          primary_color: '#00F6FF',
          secondary_color: '#3B82F6',
          primary_text_color: '#E2F8FF',
          secondary_text_color: 'rgba(226, 248, 255, 0.7)',
          background_color: '#000B1F',
          border_color: 'rgba(0, 246, 255, 0.2)',
        },
        listeners: {
          loaded: () => {
            onProcessing(false);
          },
          error: (error: any) => {
            onError(error.message || 'Payment failed');
            onProcessing(false);
            container?.remove();
          },
          close: () => {
            onProcessing(false);
            container?.remove();
          },
          payment_status: (data: any) => {
            if (data.status === 'success') {
              onSuccess(data.tx_id);
            }
          },
        },
        extra: {
          item_info: {
            author_image_url: 'https://quantumgenesis.io/logo.png',
            author: 'Quantum Genesis',
            image_url: 'https://quantumgenesis.io/token.png',
            name: 'QGEN Token',
          },
        },
      };

      const wertWidget = new WertWidget(widgetConfig);
      wertWidget.mount();
    } catch (error: any) {
      onError(error.message || 'Failed to initialize payment');
      onProcessing(false);
      document.getElementById('wert-widget-overlay')?.remove();
    }
  };

  return { handlePayment };
}